import React, { useState } from 'react';
import { ArrowLeft, Check, X, Edit3, Lightbulb, AlertTriangle, Activity, BarChart3, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTradingInsights, useInsightAction } from '../hooks/useTradingAnalysis';
import type { LearningInsight } from '../types/trading';

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/30',
  applied: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
};

const typeIcons: Record<string, React.ReactNode> = {
  pattern: <Lightbulb size={14} />,
  risk_adjustment: <AlertTriangle size={14} />,
  avoid_condition: <X size={14} />,
  calibration: <BarChart3 size={14} />,
  strategy_health: <Activity size={14} />,
  contra: <ArrowLeftRight size={14} />,
};

const typeBadgeColors: Record<string, string> = {
  pattern: 'bg-blue-500/10 text-blue-400',
  risk_adjustment: 'bg-amber-500/10 text-amber-400',
  avoid_condition: 'bg-red-500/10 text-red-400',
  calibration: 'bg-purple-500/10 text-purple-400',
  strategy_health: 'bg-orange-500/10 text-orange-400',
  contra: 'bg-rose-500/10 text-rose-400',
};

const sourceBadgeColors: Record<string, string> = {
  trade_review: 'bg-blue-500/10 text-blue-400',
  chat: 'bg-indigo-500/10 text-indigo-400',
  strategy_health: 'bg-orange-500/10 text-orange-400',
  contra: 'bg-rose-500/10 text-rose-400',
  calibration: 'bg-purple-500/10 text-purple-400',
  manual: 'bg-slate-500/10 text-slate-400',
  daily: 'bg-cyan-500/10 text-cyan-400',
  weekly: 'bg-teal-500/10 text-teal-400',
  monthly: 'bg-emerald-500/10 text-emerald-400',
};

interface Contradiction {
  existing_insight_id: string;
  existing_content: string;
  shared_topics: string[];
  reason: string;
}

const TradingInsights: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [notes, setNotes] = useState('');
  const [contradictions, setContradictions] = useState<Record<string, Contradiction[]>>({});

  const { data: insights = [], isLoading } = useTradingInsights(statusFilter);
  const actionMutation = useInsightAction();

  const handleApprove = (insight: LearningInsight, force?: boolean) => {
    actionMutation.mutate(
      {
        id: insight.id,
        action: 'approve',
        notes: notes || undefined,
        modifiedContent: editingId === insight.id ? editContent : undefined,
        force,
      },
      {
        onSuccess: (data) => {
          if (data.status === 'contradiction_detected' && data.contradictions) {
            setContradictions((prev) => ({ ...prev, [insight.id]: data.contradictions! }));
          } else {
            setContradictions((prev) => {
              const next = { ...prev };
              delete next[insight.id];
              return next;
            });
            setEditingId(null);
            setNotes('');
          }
        },
      }
    );
  };

  const handleReject = (insight: LearningInsight) => {
    actionMutation.mutate({
      id: insight.id,
      action: 'reject',
      notes: notes || undefined,
    });
    setContradictions((prev) => {
      const next = { ...prev };
      delete next[insight.id];
      return next;
    });
    setNotes('');
  };

  const startEdit = (insight: LearningInsight) => {
    setEditingId(insight.id);
    setEditContent(insight.content);
  };

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/trading')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Learning Insights</h1>
          <p className="text-slate-400 text-sm">Review and manage AI-generated trading insights</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'approved', 'applied', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === 'all' ? undefined : s)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              (s === 'all' && !statusFilter) || statusFilter === s
                ? 'bg-teal-500/20 text-teal-400'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {['pending', 'approved', 'applied', 'rejected'].map((s) => {
          const count = insights.filter((i) => i.status === s).length;
          return (
            <div key={s} className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-3 text-center">
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className="text-xs text-slate-400 capitalize">{s}</p>
            </div>
          );
        })}
      </div>

      {/* Insight List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin mx-auto" />
        </div>
      ) : insights.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Lightbulb size={32} className="mx-auto mb-3" />
          <p>No insights yet. Run a trade review to generate insights.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((insight) => (
            <div
              key={insight.id}
              className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Type badge with icon */}
                  <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${typeBadgeColors[insight.insight_type] || 'bg-slate-700 text-slate-300'}`}>
                    {typeIcons[insight.insight_type] || <Lightbulb size={12} />}
                    {insight.insight_type.replace(/_/g, ' ')}
                  </span>
                  {/* Source badge */}
                  {insight.review_period && (
                    <span className={`text-xs px-2 py-0.5 rounded ${sourceBadgeColors[insight.review_period] || 'bg-slate-700 text-slate-300'}`}>
                      {insight.review_period.replace(/_/g, ' ')}
                    </span>
                  )}
                  {/* Domain badge */}
                  {insight.domain && (
                    <span className="text-xs bg-indigo-500/10 text-indigo-400 px-2 py-0.5 rounded">
                      {insight.domain}
                    </span>
                  )}
                  {/* Symbol badge */}
                  {insight.symbol && (
                    <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{insight.symbol}</span>
                  )}
                  {/* User ID */}
                  {insight.user_id && (
                    <span className="text-xs text-slate-500">user #{insight.user_id}</span>
                  )}
                  {/* Supporting trades count */}
                  {insight.supporting_trades && insight.supporting_trades.length > 0 && (
                    <span className="text-xs text-slate-500">
                      {insight.supporting_trades.length} trade{insight.supporting_trades.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border ${statusColors[insight.status] || ''}`}>
                  {insight.status}
                </span>
              </div>

              {editingId === insight.id ? (
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-sm text-white mb-3 h-24 resize-none focus:outline-none focus:border-teal-500"
                />
              ) : (
                <p className="text-sm text-slate-300 mb-3">{insight.content}</p>
              )}

              {insight.notes && (
                <p className="text-xs text-slate-500 italic mb-2">Note: {insight.notes}</p>
              )}

              {/* Contradiction Warning */}
              {contradictions[insight.id] && (
                <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-amber-400">
                    <AlertTriangle size={12} className="inline mr-1" />
                    Potential contradiction{contradictions[insight.id].length > 1 ? 's' : ''} detected:
                  </p>
                  {contradictions[insight.id].map((c, idx) => (
                    <div key={idx} className="text-xs text-amber-300/80 pl-3 border-l-2 border-amber-500/30">
                      <p className="text-slate-300">{c.existing_content}</p>
                      <p className="text-slate-500 mt-0.5">Topics: {c.shared_topics.join(', ')}</p>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleApprove(insight, true)}
                      className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 text-xs rounded hover:bg-amber-500/30 transition-colors"
                    >
                      <Check size={12} /> Force Apply
                    </button>
                    <button
                      onClick={() => handleReject(insight)}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30 transition-colors"
                    >
                      <X size={12} /> Reject Instead
                    </button>
                  </div>
                </div>
              )}

              {insight.status === 'pending' && !contradictions[insight.id] && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700/50">
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={editingId === insight.id ? notes : ''}
                    onChange={(e) => setNotes(e.target.value)}
                    className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-teal-500"
                  />
                  <button
                    onClick={() => handleApprove(insight)}
                    className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded hover:bg-emerald-500/30 transition-colors"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    onClick={() => startEdit(insight)}
                    className="flex items-center gap-1 px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded hover:bg-slate-600 transition-colors"
                  >
                    <Edit3 size={12} /> Edit
                  </button>
                  <button
                    onClick={() => handleReject(insight)}
                    className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30 transition-colors"
                  >
                    <X size={12} /> Reject
                  </button>
                </div>
              )}

              <p className="text-xs text-slate-600 mt-2">
                {new Date(insight.timestamp.endsWith('Z') ? insight.timestamp : insight.timestamp + 'Z').toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TradingInsights;
